/* eslint-disable max-params */
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core';
import { createDialog } from '@patsnap/tahiti-core';

import type { PatentProfileWithMatchedTechFeature } from '@/domains/fto-report/types';

const patentRiskDialogService = createDialog(() => import('@/presentation/pages/fto-report/components/patent-risk-table/PatentRiskDialog.vue'));
const patentTechnicalFeatureDetailDialogService = createDialog(() => import('@/presentation/pages/fto-report/components/patent-technical-feature-detail/PatentTechnicalFeatureDetailDialog.vue'));
const claimTechnicalFeatureCompareDialogService = createDialog(() => import('@/presentation/pages/fto-report/components/claim-technical-feature-compare-table/ClaimTechnicalFeatureCompareDialog.vue'));
const patentTechnicalFeatureDetailDrawerService = createDialog(() => import('@/presentation/pages/fto-report/components/patent-technical-feature-detail/PatentTechnicalFeatureDetailDrawer.vue'));

@injectable()
export class FtoReportCommandContribution implements CommandContribution {

    private _handleOpenPatentRiskDialog(payload: { defaultAddPatent?: boolean }) {
        const instance = patentRiskDialogService();
        return instance.open(payload);
    }

    private _handleOpenPatentTechnicalFeatureDetailDialog(payload: { patentId: string, lang: string, defaultSelectedTechFeature?: string }) {
        const instance = patentTechnicalFeatureDetailDialogService();
        return instance.open(payload);
    }

    private _handleOpenClaimTechnicalFeatureCompareDialog(payload: { patentId?: string, lang?: string; matchedPatents?: PatentProfileWithMatchedTechFeature[]; defaultOpenDrawer?: boolean }) {
        const instance = claimTechnicalFeatureCompareDialogService();
        return instance.open(payload);
    }

    private _handleOpenPatentTechnicalFeatureDetailDrawer() {
        const instance = patentTechnicalFeatureDetailDrawerService();
        return instance.open();
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'ftoReport:open-edit-compare-list-dialog',
        }, {
            execute: async (payload: { defaultAddPatent?: boolean }) => {
                return await this._handleOpenPatentRiskDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'ftoReport:open-patent-technical-feature-detail-dialog',
        }, {
            execute: async (payload: { patentId: string, lang: string, defaultSelectedTechFeature?: string }) => {
                return await this._handleOpenPatentTechnicalFeatureDetailDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'ftoReport:open-claim-technical-feature-compare-dialog',
        }, {
            execute: async (payload: { patentId: string, lang: string; matchedPatents?: PatentProfileWithMatchedTechFeature[]; defaultOpenDrawer?: boolean }) => {
                return await this._handleOpenClaimTechnicalFeatureCompareDialog(payload);
            },
        });
        commands.registerCommand({
            id: 'ftoReport:open-patent-technical-feature-detail-drawer',
        }, {
            execute: async () => {
                return await this._handleOpenPatentTechnicalFeatureDetailDrawer();
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开编辑对比列表对话框
         * @param commandId - 命令ID
         * @param payload - 对话框参数
         */
        executeCommand(commandId: 'ftoReport:open-edit-compare-list-dialog', payload: { defaultAddPatent?: boolean }): void;
        /**
         * 打开专利技术特征详情对话框
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'ftoReport:open-patent-technical-feature-detail-dialog',
            payload: {
                patentId: string,
                lang: string,
                defaultSelectedTechFeature?: string,
            }
        ): void;
        /**
         * 打开权利要求技术特征对比对话框
         * @param commandId - 命令ID
         * @param payload - 对话框参数
         */
        executeCommand(commandId: 'ftoReport:open-claim-technical-feature-compare-dialog',
            payload: {
                patentId: string,
                lang: string,
            }
        ): void;
        /**
         * 打开专利技术特征详情抽屉
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'ftoReport:open-patent-technical-feature-detail-drawer'): void;
    }
}
